import { Navigate, Outlet } from 'react-router-dom';

import { ROUTE_PATHS } from '@/app/router/paths';
import { Spinner } from '@/components/ui/spinner';

import { useAuth } from '../hooks/useAuth';

/**
 * Inverse guard for routes that only make sense while signed out, such as the
 * login page. Prevents an authenticated user from landing back on a login form.
 *
 * `SessionGate` already blocks every route until the initial probe settles,
 * so in practice this never renders before `isSessionResolved` is true. The
 * check is kept anyway as a second, independent line of defence — waiting
 * rather than redirecting while unresolved, since a signed-out visitor headed
 * here legitimately would otherwise bounce to `/dashboard` and immediately
 * back to `/login`, flashing both in the process instead of neither.
 */
export function RequireGuest() {
  const { isAuthenticated, isSessionResolved } = useAuth();

  if (!isSessionResolved) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner size="lg" label="Checking your session" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.dashboard} replace />;
  }

  return <Outlet />;
}
